// Validate user SVG. We REJECT (not silently strip) anything that is not a
// plain vector-paths file, so buyers learn what to fix. Only geometry is ever
// extracted from it (via SVGLoader); the SVG is never inserted into the DOM.
import {LIMITS} from '../imports/config.js'

const ALLOWED = new Set([
  'svg',
  'g',
  'path',
  'rect',
  'circle',
  'ellipse',
  'line',
  'polyline',
  'polygon',
  'defs',
  'title',
  'desc',
  'metadata',
])
const STRUCTURAL = new Set(['svg', 'g', 'defs', 'title', 'desc', 'metadata'])
/** @type {Record<string, string>} */
const HINT = {
  text: 'contains <text>; convert text to outlines/paths first',
  image: 'contains an embedded <image>; only vector paths are allowed',
  script: 'contains a <script>, which is not allowed',
  foreignObject: 'contains <foreignObject>, which is not allowed',
  use: 'contains <use>; expand/ungroup it first',
  style: 'contains <style>; use plain fill attributes instead',
}

export class SvgError extends Error {}

/**
 * @param {unknown} source raw SVG text
 * @returns {string} the source (unchanged) if valid; throws SvgError otherwise
 */
export function validateSvg(source) {
  if (typeof source !== 'string' || !source.trim()) throw new SvgError('The file is empty.')
  if (source.length > LIMITS.svgMaxBytes)
    throw new SvgError(`File is too large (max ${Math.round(LIMITS.svgMaxBytes / 1024)} KB).`)
  if (/<!ENTITY|<!DOCTYPE[^>]*\[/i.test(source))
    throw new SvgError('SVG contains entity declarations, which are not allowed.')
  if (!/<svg[\s>]/i.test(source)) throw new SvgError('This does not look like an SVG file.')

  const doc = new DOMParser().parseFromString(source, 'image/svg+xml')
  if (doc.getElementsByTagName('parsererror').length) throw new SvgError('The SVG could not be parsed (invalid XML).')
  const root = doc.documentElement
  if (!root || root.localName !== 'svg') throw new SvgError('The root element must be <svg>.')

  const all = [root, ...root.querySelectorAll('*')]
  if (all.length > LIMITS.svgMaxElements)
    throw new SvgError(`Too many elements (${all.length}; max ${LIMITS.svgMaxElements}).`)
  let drawable = 0
  for (const el of all) {
    const name = el.localName
    if (!ALLOWED.has(name))
      throw new SvgError(
        HINT[name]
          ? `The SVG ${HINT[name]}.`
          : `Unsupported element <${name}>; only paths and basic shapes are allowed.`,
      )
    if (!STRUCTURAL.has(name)) drawable++
    for (const attr of el.attributes) {
      const n = attr.name.toLowerCase()
      if (n.startsWith('on')) throw new SvgError('The SVG contains event-handler attributes, which are not allowed.')
      if (n === 'href' || n.endsWith(':href'))
        throw new SvgError('The SVG contains links/references, which are not allowed.')
      if (/javascript:|data:text\/html/i.test(attr.value))
        throw new SvgError('The SVG contains script content, which is not allowed.')
      if (n === 'style' && /url\(|expression\(|@import/i.test(attr.value))
        throw new SvgError('The SVG contains unsupported style content.')
    }
  }
  if (!drawable) throw new SvgError('The SVG has no shapes (paths, rectangles, circles, …).')
  return source
}
